import { useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

export interface NavigateWithQueryOptions {
  /**
   * Whether to preserve all current query parameters
   * @default true
   */
  preserveQuery?: boolean;

  /**
   * Additional query parameters to add/override
   */
  query?: Record<string, string | number | boolean | null | undefined>;

  /**
   * Query parameters to exclude from preservation
   */
  excludeParams?: string[];

  /**
   * Whether to replace the current history entry instead of pushing a new one
   * @default false
   */
  replace?: boolean;

  /**
   * State to pass to the target route via history state
   */
  state?: unknown;
}

export type NavigateWithQuery = {
  /**
   * Navigate to a path while preserving current query parameters
   * @param to - The path to navigate to
   * @param options - Navigation options
   */
  (to: string, options?: NavigateWithQueryOptions): void;

  /**
   * Navigate to a path with only specific query parameters (no preservation)
   * @param to - The path to navigate to
   * @param query - Query parameters to include
   * @param replace - Whether to replace current history entry
   */
  (to: string, query: Record<string, string | number | boolean | null | undefined>, replace?: boolean): void;

  /**
   * Navigate back/forward in history
   * @param delta - Number of entries to go back (negative) or forward (positive)
   */
  (delta: number): void;
};

/**
 * Custom hook that extends useNavigate to preserve query parameters when navigating
 * 
 * @returns Enhanced navigate function that preserves query parameters
 * 
 * @example
 * ```typescript
 * const navigate = useNavigateWithQuery();
 * 
 * // Navigate while preserving all current query params
 * navigate('/dashboard');
 * 
 * // Navigate with additional query params
 * navigate('/users', { query: { status: 'active' } });
 * 
 * // Navigate excluding specific params
 * navigate('/settings', { excludeParams: ['temp', 'draft'] });
 * 
 * // Navigate with only specific params (no preservation)
 * navigate('/search', { q: 'react', page: '1' });
 * ```
 */
export const useNavigateWithQuery = (): NavigateWithQuery => {
  const navigate = useNavigate();
  const location = useLocation();
  
  const navigateWithQuery = useCallback((
    to: string | number,
    optionsOrQuery?: NavigateWithQueryOptions | Record<string, string | number | boolean | null | undefined>,
    replace?: boolean
  ) => {
    // Handle numeric navigation (go back/forward)
    if (typeof to === 'number') {
      navigate(to);
      return;
    }

    // Handle overload: if second param is not NavigateWithQueryOptions, treat as query object
    if (optionsOrQuery && !('preserveQuery' in optionsOrQuery || 'query' in optionsOrQuery || 'excludeParams' in optionsOrQuery || 'replace' in optionsOrQuery || 'state' in optionsOrQuery)) {
      // Second overload: direct query object
      const queryParams = optionsOrQuery as Record<string, string | number | boolean | null | undefined>;
      const searchParams = new URLSearchParams();
      
      Object.entries(queryParams).forEach(([key, value]) => {
        if (value !== null && value !== undefined) {
          searchParams.set(key, String(value));
        }
      });
      
      const queryString = searchParams.toString();
      const fullPath = queryString ? `${to}?${queryString}` : to;
      
      navigate(fullPath, { replace: replace || false });
      return;
    }
    
    // First overload: with options
    const options = (optionsOrQuery as NavigateWithQueryOptions) || {};
    const {
      preserveQuery = true,
      query = {},
      excludeParams = [],
      replace: replaceOption = false,
      state,
    } = options;
    
    const searchParams = new URLSearchParams();
    
    // Preserve current query parameters if enabled
    if (preserveQuery) {
      const currentParams = new URLSearchParams(location.search);
      
      currentParams.forEach((value, key) => {
        // Skip excluded parameters
        if (!excludeParams.includes(key)) {
          searchParams.set(key, value);
        }
      });
    }
    
    // Add/override with new query parameters
    Object.entries(query).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        searchParams.set(key, String(value));
      } else {
        // Remove parameter if value is null/undefined
        searchParams.delete(key);
      }
    });
    
    const queryString = searchParams.toString();
    const fullPath = queryString ? `${to}?${queryString}` : to;
    
    navigate(fullPath, { replace: replaceOption, state });
  }, [navigate, location.search]);
  
  return navigateWithQuery as NavigateWithQuery;
};

/**
 * Utility function to get current query parameters as an object
 * @param search - Optional search string, defaults to current location search
 * @returns Object with query parameters
 */
export const useCurrentQuery = (search?: string): Record<string, string> => {
  const location = useLocation();
  const searchString = search || location.search;
  
  return Object.fromEntries(new URLSearchParams(searchString));
};

/**
 * Hook to get and set a specific query parameter
 * @param key - The query parameter key
 * @returns Tuple of [value, setValue] similar to useState
 */
export const useQueryParam = (key: string): [string | null, (value: string | null) => void] => {
  const navigate = useNavigateWithQuery();
  const location = useLocation();
  
  const getValue = useCallback(() => {
    const params = new URLSearchParams(location.search);
    return params.get(key);
  }, [location.search, key]);
  
  const setValue = useCallback((value: string | null) => {
    const params = new URLSearchParams(location.search);
    
    if (value === null) {
      params.delete(key);
    } else {
      params.set(key, value);
    }
    
    const queryString = params.toString();
    const newPath = queryString ? `${location.pathname}?${queryString}` : location.pathname;
    
    navigate(newPath, { preserveQuery: false, replace: true });
  }, [navigate, location.pathname, location.search, key]);
  
  return [getValue(), setValue];
};
